import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Coins, TrendingUp, TrendingDown, PieChart, BarChart3, 
  Eye, ArrowUpRight, ArrowDownRight, Shield, Calendar
} from 'lucide-react';
import api from '../../lib/api';
import { ProofOfReserveBadge } from '../../components/tokenization/ProofOfReserveBadge';

interface PortfolioSummary {
  totalValue: number;
  totalInvested: number;
  totalProfitLoss: number;
  totalProfitLossPercent: number;
  totalYieldReceived: number;
  holdingsCount: number;
}

interface TokenHolding {
  tokenId: string;
  name: string;
  symbol: string;
  category: string;
  quantity: number;
  unitPrice: number;
  currentValue: number;
  avgBuyPrice: number;
  profitLoss: number;
  profitLossPercent: number;
  porCoverage: number;
  yieldRate: number | null;
  nextYieldDate: string | null;
  lockedQuantity: number;
  lockUpEndDate: string | null;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundo',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios',
};

export function TokenPortfolio() {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('value');

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['portfolio-summary'],
    queryFn: async () => {
      const res = await api.get('/tokenization/portfolio/summary');
      return res.data as PortfolioSummary;
    },
  });

  const { data: holdings, isLoading: holdingsLoading } = useQuery({
    queryKey: ['portfolio-holdings', sortBy],
    queryFn: async () => {
      const res = await api.get(`/tokenization/portfolio/holdings?sortBy=${sortBy}`);
      return res.data as TokenHolding[];
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isLoading = summaryLoading || holdingsLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Meu Portfólio de Tokens</h1>
          <p className="text-surface-400">Acompanhe seus investimentos em ativos tokenizados</p>
        </div>
        <button
          onClick={() => navigate('/banking/tokens')}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
        >
          Explorar Tokens
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/20 rounded-lg">
                <Coins className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalValue)}</p>
                <p className="text-sm text-surface-400">Valor Total</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                summary.totalProfitLoss >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'
              }`}>
                {summary.totalProfitLoss >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-green-400" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-400" />
                )}
              </div>
              <div>
                <p className={`text-2xl font-bold ${
                  summary.totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {summary.totalProfitLoss >= 0 ? '+' : ''}{formatCurrency(summary.totalProfitLoss)}
                </p>
                <p className="text-sm text-surface-400">
                  Lucro/Prejuízo ({summary.totalProfitLossPercent.toFixed(2)}%)
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.totalYieldReceived)}</p>
                <p className="text-sm text-surface-400">Rendimentos Recebidos</p>
              </div>
            </div>
          </div>

          <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <PieChart className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.holdingsCount}</p>
                <p className="text-sm text-surface-400">Tokens no Portfólio</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Holdings */}
      <div className="bg-surface-900 rounded-xl border border-surface-800">
        <div className="flex items-center justify-between p-4 border-b border-surface-800">
          <h2 className="font-semibold">Minhas Posições</h2>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 bg-surface-800 border border-surface-700 rounded-lg text-sm"
          >
            <option value="value">Maior Valor</option>
            <option value="profit">Maior Lucro</option>
            <option value="loss">Maior Prejuízo</option>
            <option value="yield">Maior Rendimento</option>
          </select>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-surface-400">Carregando portfólio...</p>
          </div>
        ) : !holdings?.length ? (
          <div className="p-8 text-center">
            <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
            <p className="text-surface-400 mb-4">Você ainda não possui tokens</p>
            <button
              onClick={() => navigate('/banking/tokens')}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              Explorar Marketplace
            </button>
          </div>
        ) : (
          <div className="divide-y divide-surface-800">
            {holdings.map((holding) => (
              <div
                key={holding.tokenId}
                className="p-4 hover:bg-surface-800/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/banking/tokens/${holding.tokenId}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                      <Coins className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{holding.name}</p>
                        <span className="text-sm text-surface-400">{holding.symbol}</span>
                        <ProofOfReserveBadge coverage={holding.porCoverage} size="sm" />
                      </div>
                      <div className="flex items-center gap-3 text-sm text-surface-400">
                        <span>{holding.quantity.toLocaleString('pt-BR')} tokens</span>
                        <span>•</span>
                        <span>{categoryLabels[holding.category] || holding.category}</span>
                        {holding.lockedQuantity > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-yellow-400">
                              {holding.lockedQuantity} em lock-up
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(holding.currentValue)}</p>
                    <div className={`flex items-center justify-end gap-1 text-sm ${
                      holding.profitLoss >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {holding.profitLoss >= 0 ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4" />
                      )}
                      <span>
                        {holding.profitLoss >= 0 ? '+' : ''}{formatCurrency(holding.profitLoss)} ({holding.profitLossPercent.toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="flex items-center gap-6 mt-3 pt-3 border-t border-surface-800 text-sm">
                  <div>
                    <span className="text-surface-400">Preço Médio: </span>
                    <span>{formatCurrency(holding.avgBuyPrice)}</span>
                  </div>
                  <div>
                    <span className="text-surface-400">Preço Atual: </span>
                    <span>{formatCurrency(holding.unitPrice)}</span>
                  </div>
                  {holding.yieldRate && (
                    <div>
                      <span className="text-surface-400">Rendimento: </span>
                      <span className="text-green-400">{holding.yieldRate.toFixed(2)}% a.a.</span>
                    </div>
                  )}
                  {holding.nextYieldDate && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-surface-400" />
                      <span className="text-surface-400">Próx. pagamento: </span>
                      <span>{formatDate(holding.nextYieldDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => navigate('/banking/tokens/orders')}
          className="flex items-center gap-3 p-4 bg-surface-900 rounded-xl border border-surface-800 hover:border-primary-500/50 transition-colors"
        >
          <BarChart3 className="w-6 h-6 text-primary-400" />
          <div className="text-left">
            <p className="font-medium">Histórico de Ordens</p>
            <p className="text-sm text-surface-400">Ver todas as ordens</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/banking/tokens/yields')}
          className="flex items-center gap-3 p-4 bg-surface-900 rounded-xl border border-surface-800 hover:border-primary-500/50 transition-colors"
        >
          <TrendingUp className="w-6 h-6 text-green-400" />
          <div className="text-left">
            <p className="font-medium">Histórico de Rendimentos</p>
            <p className="text-sm text-surface-400">Ver pagamentos recebidos</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/banking/tokens/redeem')}
          className="flex items-center gap-3 p-4 bg-surface-900 rounded-xl border border-surface-800 hover:border-primary-500/50 transition-colors"
        >
          <ArrowDownRight className="w-6 h-6 text-yellow-400" />
          <div className="text-left">
            <p className="font-medium">Resgatar Tokens</p>
            <p className="text-sm text-surface-400">Solicitar resgate</p>
          </div>
        </button>
      </div>
    </div>
  );
}

export default TokenPortfolio;
